// One way to call the staff endpoints that check who is asking: Messages,
// work items, clinic orders, the compliance screens and the users search
// (backend/api/request_auth.py). Every call sends the signed-in user's Supabase
// access token (the token source authedFetch uses), and the server takes the
// caller from it; never from a body.

import { getSupabase } from "../components/supabaseClient";
import { getAccessTokenSafe, SESSION_EXPIRED } from "./orderPaperwork";
import { isImpersonating } from "../utils/impersonation";

// A fresh access token from the refresh token Supabase still holds (the stored
// one rotates out from under a long-open tab), or null. Never in a "view as"
// tab: its session holds no real refresh token, so a refresh always fails, and
// auth-js then drops the session, which would end the view-as.
export async function refreshedToken() {
  if (isImpersonating()) return null;
  try {
    const supabase = await getSupabase();
    const { data } = (await supabase?.auth?.refreshSession()) || {};
    return data?.session?.access_token || null;
  } catch (err) {
    return null;
  }
}

export async function currentToken() {
  return (await getAccessTokenSafe()) || (await refreshedToken());
}

// `url` with the token. Never sent without one: that is a sure 401. One 401 is
// retried once with a refreshed token, as authedFetch does; a second reads as
// an expired session. Resolves to the JSON the server answered; a failure
// throws the server's `error`, or `failure` when it gave none.
//
// `form` (a FormData, such as a file upload) is sent as it is, in place of a
// JSON `body`; the browser sets its multipart boundary. With `raw` the answer
// that isn't a failure resolves to the Response itself, unread: for a file the
// server sends (a CSV export) rather than JSON.
export async function staffRequest(
  url,
  { method = "GET", body, form, signal, raw = false, failure = "Request failed" } = {},
) {
  const token = await currentToken();
  if (!token) throw new Error(SESSION_EXPIRED);
  const json = body !== undefined && form === undefined;
  const send = (bearer) =>
    fetch(url, {
      method,
      headers: {
        ...(json ? { "Content-Type": "application/json" } : {}),
        Authorization: `Bearer ${bearer}`,
      },
      credentials: "include",
      signal,
      ...(form !== undefined ? { body: form } : json ? { body: JSON.stringify(body) } : {}),
    });
  let res = await send(token);
  if (res.status === 401) {
    const fresh = await refreshedToken();
    if (!fresh) throw new Error(SESSION_EXPIRED);
    res = await send(fresh);
    if (res.status === 401) throw new Error(SESSION_EXPIRED);
  }
  if (raw && res.ok) return res;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText || failure);
  return data;
}
